import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Trash2, Mail, ShieldCheck, Clock } from 'lucide-react';

const APP_NAME = 'Теория';
const EMAIL = 'manager@theorybar.ru';
const COMPANY = 'ООО «Ресторан Теория»';

const RETENTION_RU = '30 дней';
const RETENTION_EN = '30 days';

function RuContent() {
  return (
    <div className="space-y-8 text-[15px] leading-relaxed text-gray-700">
      <section className="bg-red-50 border border-red-100 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <Trash2 className="text-red-500 mt-0.5 shrink-0" size={20} />
          <div>
            <p className="font-semibold text-red-700 mb-1">Удаление аккаунта в приложении «{APP_NAME}»</p>
            <p className="text-red-600 text-sm">
              После удаления аккаунта восстановление данных невозможно.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Как запросить удаление аккаунта</h2>
        <ol className="space-y-4">
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white text-sm font-bold flex items-center justify-center">1</span>
            <div>
              <p className="font-medium text-gray-900">Откройте приложение «{APP_NAME}»</p>
              <p className="text-gray-500 text-sm mt-0.5">Убедитесь, что вы авторизованы в своём аккаунте</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white text-sm font-bold flex items-center justify-center">2</span>
            <div>
              <p className="font-medium text-gray-900">Перейдите в Настройки</p>
              <p className="text-gray-500 text-sm mt-0.5">Нажмите на иконку профиля или вкладку «Настройки» в нижнем меню</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white text-sm font-bold flex items-center justify-center">3</span>
            <div>
              <p className="font-medium text-gray-900">Нажмите «Удалить аккаунт»</p>
              <p className="text-gray-500 text-sm mt-0.5">Подтвердите своё намерение в появившемся диалоге</p>
            </div>
          </li>
        </ol>
      </section>

      <section className="bg-gray-50 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={18} className="text-gray-600" />
          <h2 className="text-base font-semibold text-gray-900">Альтернативный способ — по email</h2>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Если у вас нет доступа к приложению, отправьте запрос на удаление аккаунта напрямую:
        </p>
        <a
          href={`mailto:${EMAIL}?subject=Запрос на удаление аккаунта&body=Пожалуйста, удалите мой аккаунт и все связанные данные. Мой номер телефона: `}
          className="inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gray-700 transition-colors"
        >
          <Mail size={15} />
          {EMAIL}
        </a>
        <p className="text-xs text-gray-400 mt-3">
          Укажите в письме номер телефона, привязанный к аккаунту. Мы обработаем запрос в течение 3 рабочих дней.
        </p>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Что происходит с данными</h2>
        </div>
        <div className="space-y-3">
          <div className="flex gap-3 p-4 bg-red-50 rounded-xl">
            <Trash2 size={17} className="text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Удаляется в течение {RETENTION_RU}:</p>
              <ul className="mt-1 text-sm text-gray-600 space-y-0.5">
                <li>• Профиль (номер телефона, имя)</li>
                <li>• Сохранённые адреса доставки</li>
                <li>• История заказов</li>
                <li>• FCM-токены устройств (push-уведомления)</li>
              </ul>
            </div>
          </div>
          <div className="flex gap-3 p-4 bg-amber-50 rounded-xl">
            <Clock size={17} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Хранится согласно законодательству:</p>
              <ul className="mt-1 text-sm text-gray-600 space-y-0.5">
                <li>• Финансовые документы и чеки — 5 лет (ФЗ‑54)</li>
                <li>• Анонимизированная статистика заказов</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="text-sm text-gray-400 border-t border-gray-100 pt-6">
        <p>{COMPANY}</p>
        <p>Email: <a href={`mailto:${EMAIL}`} className="text-blue-500 underline">{EMAIL}</a></p>
      </section>
    </div>
  );
}

function EnContent() {
  return (
    <div className="space-y-8 text-[15px] leading-relaxed text-gray-700">
      <section className="bg-red-50 border border-red-100 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <Trash2 className="text-red-500 mt-0.5 shrink-0" size={20} />
          <div>
            <p className="font-semibold text-red-700 mb-1">Delete your «{APP_NAME}» account</p>
            <p className="text-red-600 text-sm">
              Account deletion is permanent and cannot be undone.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">How to request account deletion</h2>
        <ol className="space-y-4">
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white text-sm font-bold flex items-center justify-center">1</span>
            <div>
              <p className="font-medium text-gray-900">Open the «{APP_NAME}» app</p>
              <p className="text-gray-500 text-sm mt-0.5">Make sure you are logged into your account</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white text-sm font-bold flex items-center justify-center">2</span>
            <div>
              <p className="font-medium text-gray-900">Go to Settings</p>
              <p className="text-gray-500 text-sm mt-0.5">Tap the profile icon or the «Settings» tab in the bottom menu</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white text-sm font-bold flex items-center justify-center">3</span>
            <div>
              <p className="font-medium text-gray-900">Tap «Delete account»</p>
              <p className="text-gray-500 text-sm mt-0.5">Confirm your intention in the dialog that appears</p>
            </div>
          </li>
        </ol>
      </section>

      <section className="bg-gray-50 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={18} className="text-gray-600" />
          <h2 className="text-base font-semibold text-gray-900">Alternative — request by email</h2>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          If you cannot access the app, send a deletion request directly:
        </p>
        <a
          href={`mailto:${EMAIL}?subject=Account deletion request&body=Please delete my account and all associated data. My phone number: `}
          className="inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gray-700 transition-colors"
        >
          <Mail size={15} />
          {EMAIL}
        </a>
        <p className="text-xs text-gray-400 mt-3">
          Please include the phone number linked to your account. We will process your request within 3 business days.
        </p>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">What happens to your data</h2>
        </div>
        <div className="space-y-3">
          <div className="flex gap-3 p-4 bg-red-50 rounded-xl">
            <Trash2 size={17} className="text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Deleted within {RETENTION_EN}:</p>
              <ul className="mt-1 text-sm text-gray-600 space-y-0.5">
                <li>• Profile (phone number, name)</li>
                <li>• Saved delivery addresses</li>
                <li>• Order history</li>
                <li>• Device FCM tokens (push notifications)</li>
              </ul>
            </div>
          </div>
          <div className="flex gap-3 p-4 bg-amber-50 rounded-xl">
            <Clock size={17} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Retained as required by law:</p>
              <ul className="mt-1 text-sm text-gray-600 space-y-0.5">
                <li>• Financial records and receipts — 5 years (Federal Law No. 54-FZ)</li>
                <li>• Anonymised order statistics</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="text-sm text-gray-400 border-t border-gray-100 pt-6">
        <p>{COMPANY}</p>
        <p>Email: <a href={`mailto:${EMAIL}`} className="text-blue-500 underline">{EMAIL}</a></p>
      </section>
    </div>
  );
}

export default function DeleteAccountScreen() {
  const navigate = useNavigate();
  const [lang, setLang] = useState<'ru' | 'en'>('ru');

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 pb-16">
        <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between py-4 z-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft size={20} />
            <span className="text-sm">Назад</span>
          </button>

          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-sm font-medium">
            <button
              onClick={() => setLang('ru')}
              className={`px-3 py-1.5 transition-colors ${lang === 'ru' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              RU
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1.5 transition-colors ${lang === 'en' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              EN
            </button>
          </div>
        </div>

        <div className="py-8">
          <h1 className="text-2xl font-bold text-gray-900">
            {lang === 'ru' ? 'Удаление аккаунта' : 'Delete Account'}
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            {lang === 'ru' ? `Приложение «${APP_NAME}»` : `«${APP_NAME}» app`}
          </p>
        </div>

        {lang === 'ru' ? <RuContent /> : <EnContent />}
      </div>
    </div>
  );
}
